import { useBasicInfo } from '../_shared/contexts/BasicInfoContext';
import { useUserData } from '@/src/listings/hooks/useUserData';
import { WaitingRoomBanner, CreatorBanner } from './notificationBannerUseCases';
import { useWaitingRoomStatus } from '@/src/listings/hooks/useWaitingRoomStatus';
import { useState } from 'react';
import { NotificationBannerFrame } from './NotificationBannerFrame';
import { useDisplayStatus } from '../_shared/contexts/DisplayStatusContext';
import { useTicketInformation } from '@/src/listings/hooks/useTicketInformation/useTicketInformation';
import { shouldShowCheckoutButton } from '../ConversionBar/utils';
import { QUERY_PARAMS } from '@/src/listings/common/constants';
import { getWindowObject, HAS_WINDOW } from '@eventbrite/feature-detection';

type NotificationBannerProps = {
  priorityBanner?: React.ReactNode;
};

export const NotificationBanner: React.FC<NotificationBannerProps> = ({ priorityBanner }) => {
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const { organizationId, status } = useBasicInfo();
  const { canManageEvent, isLoading: isUserDataLoading } = useUserData();
  const { displayStatus } = useDisplayStatus();
  const [ticketInfo, isTicketInformationLoading] = useTicketInformation();
  const { waitingRoomStatus, isLoading: isWaitingRoomStatusLoading } = useWaitingRoomStatus({
    organizationId: organizationId ?? '',
  });
  const isLoading = isUserDataLoading || isWaitingRoomStatusLoading;
  const waitlistSearchParams = HAS_WINDOW ? new URLSearchParams(getWindowObject('location')?.search ?? '') : null;
  const hasReleasedWaitlistClaim = Boolean(waitlistSearchParams?.get(QUERY_PARAMS.WAITLIST_TOKEN)?.trim().length);
  const canShowWaitingRoomBanner =
    waitingRoomStatus?.activated &&
    !isTicketInformationLoading &&
    shouldShowCheckoutButton(status, displayStatus, ticketInfo, hasReleasedWaitlistClaim);

  const closeBanner = () => {
    setIsBannerVisible(false);
  };

  if (priorityBanner) {
    return <NotificationBannerFrame>{priorityBanner}</NotificationBannerFrame>;
  }

  const eligibleBanners = [
    canManageEvent && <CreatorBanner />,
    canShowWaitingRoomBanner && <WaitingRoomBanner onCloseClick={closeBanner} />,
  ].filter(Boolean);

  if (isLoading || eligibleBanners.length === 0 || !isBannerVisible) return null;

  return <NotificationBannerFrame>{eligibleBanners.length > 0 ? eligibleBanners[0] : null}</NotificationBannerFrame>;
};
